import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  UploadCloud,
  FileCheck2,
  Trash2,
  FileSignature,
  Layers,
  StickyNote,
  Type,
  Hash,
  CalendarDays,
} from "lucide-react";
import Select from "react-select";
import { getAllContracts } from "../../../api/crm/contracts/info";
import { createContractDocument } from "../../../api/crm/contract-documents/info";
import {
  showConfirmDialog,
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { DOC_TYPE_OPTIONS } from "./contractDocumentUtils";

const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const SectionHeader = ({ icon: Icon, tone, title, subtitle }) => (
  <div className="mb-5 flex items-center gap-3">
    <div className={`rounded-2xl p-3 ${tone}`}>
      <Icon size={18} />
    </div>
    <div>
      <h3 className="text-sm font-black uppercase tracking-[0.16em] text-gray-700">{title}</h3>
      <p className="text-xs font-medium text-gray-400">{subtitle}</p>
    </div>
  </div>
);

const fieldLabel =
  "ml-1 block text-[10px] font-black uppercase tracking-[0.18em] text-gray-400";
const inputBase =
  "w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 outline-none transition focus:border-red-400 focus:bg-white";

const ContractLinkedDossierUploadModal = ({
  isOpen,
  onClose,
  onSuccess,
  defaultContractId = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState(defaultContractId || "");
  const [docType, setDocType] = useState("other");
  const [title, setTitle] = useState("");
  const [docCode, setDocCode] = useState("");
  const [docDate, setDocDate] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleCancelRef = useRef(() => {});
  const isCancelingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setContractId(defaultContractId || "");
    setDocType("other");
    setTitle("");
    setDocCode("");
    setDocDate("");
    setNote("");
    setFile(null);

    getAllContracts({ limit: 500 })
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setContracts(items);
      })
      .catch(() => setContracts([]));
  }, [isOpen, defaultContractId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEsc = (e) => {
      if (e.key === "Escape") handleCancelRef.current();
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  const contractOptions = contracts.map((c) => ({
    value: c.id,
    label: `${c.contract_code || "—"} — ${c.title || "Không có tiêu đề"}`,
  }));

  const selectStyles = {
    ...customSelectStyles,
    control: (base, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(base, state)
        : base),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
      backgroundColor: "#f9fafb",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const applyFile = (picked) => {
    if (!picked) return;
    setFile(picked);
    setTitle((prev) => {
      if (prev.trim()) return prev;
      return picked.name.replace(/\.[^/.]+$/, "");
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) applyFile(dropped);
  };

  const isDirty = () =>
    !!file ||
    contractId !== (defaultContractId || "") ||
    docType !== "other" ||
    title.trim() !== "" ||
    docCode.trim() !== "" ||
    docDate !== "" ||
    note.trim() !== "";

  const handleCancel = async () => {
    if (loading || isCancelingRef.current) return;

    if (!isDirty()) {
      onClose();
      return;
    }

    isCancelingRef.current = true;
    const confirm = await showConfirmDialog({
      title: "Xác nhận huỷ",
      text: "Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn huỷ thao tác này?",
      confirmText: "Đồng ý",
      cancelText: "Giữ lại",
    });
    isCancelingRef.current = false;

    if (confirm.isConfirmed) onClose();
  };

  handleCancelRef.current = handleCancel;

  const handleSubmit = async () => {
    if (!contractId) {
      showErrorToast("Vui lòng chọn hợp đồng");
      return;
    }
    if (!file) {
      showErrorToast("Vui lòng chọn tệp hồ sơ");
      return;
    }
    if (!title.trim()) {
      showErrorToast("Vui lòng nhập tiêu đề hồ sơ");
      return;
    }

    const formData = new FormData();
    formData.append("contract_id", contractId);
    formData.append("doc_type", docType);
    formData.append("title", title.trim());
    if (docCode.trim()) formData.append("doc_code", docCode.trim());
    if (docDate) formData.append("doc_date", docDate);
    if (note.trim()) formData.append("note", note.trim());
    formData.append("file", file);

    try {
      setLoading(true);
      await createContractDocument(formData);
      showSuccessToast("Đã tải lên hồ sơ liên kết");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải lên hồ sơ");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9998] bg-slate-900/50 backdrop-blur-sm"
        onClick={handleCancel}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 24 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[2rem] border border-white/60 bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative overflow-hidden border-b border-gray-100 bg-gradient-to-r from-red-700 via-red-700 to-red-800 px-8 py-7 text-white">
            <div className="absolute -right-10 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute bottom-0 right-14 h-24 w-24 rounded-full bg-black/10 blur-2xl" />

            <div className="relative flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.5rem] bg-white text-red-700 shadow-xl shadow-red-950/20">
                <UploadCloud className="h-8 w-8" />
              </div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.24em] text-red-100">
                  Hồ sơ liên kết
                </p>
                <h2 className="mt-1.5 text-2xl font-black tracking-tight">Tải lên hồ sơ liên kết</h2>
                <p className="mt-1.5 text-sm font-medium text-red-100/90">
                  Gắn chứng từ, phụ lục vào hợp đồng để lưu trữ và tra cứu tập trung.
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50/40 p-6 sm:p-8">
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
              {/* Cột trái: Thông tin hồ sơ */}
              <section className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm">
                <SectionHeader
                  icon={FileSignature}
                  tone="bg-blue-50 text-blue-600"
                  title="Thông tin hồ sơ"
                  subtitle="Gắn với hợp đồng và phân loại"
                />

                <div className="space-y-4">
                  <div>
                    <label className={fieldLabel}>
                      <span className="inline-flex items-center gap-1.5">
                        <Type size={12} />
                        Tiêu đề hồ sơ *
                      </span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className={`mt-1 ${inputBase}`}
                      placeholder="VD: Hợp đồng đã ký, Biên bản nghiệm thu..."
                    />
                  </div>

                  <div>
                    <label className={fieldLabel}>Hợp đồng liên kết *</label>
                    <div className="mt-1">
                      <Select
                        options={contractOptions}
                        value={contractOptions.find((o) => o.value === contractId) || null}
                        onChange={(opt) => setContractId(opt?.value || "")}
                        placeholder="Chọn hợp đồng..."
                        styles={selectStyles}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={fieldLabel}>
                        <span className="inline-flex items-center gap-1.5">
                          <Hash size={12} />
                          Số hiệu chứng từ
                        </span>
                      </label>
                      <input
                        type="text"
                        value={docCode}
                        onChange={(e) => setDocCode(e.target.value)}
                        className={`mt-1 ${inputBase}`}
                        placeholder="VD: HD-2026-001"
                      />
                    </div>
                    <div>
                      <label className={fieldLabel}>
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays size={12} />
                          Ngày chứng từ
                        </span>
                      </label>
                      <input
                        type="date"
                        value={docDate}
                        onChange={(e) => setDocDate(e.target.value)}
                        className={`mt-1 ${inputBase}`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={fieldLabel}>Loại hồ sơ</label>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="rounded-2xl bg-slate-100 p-3 text-slate-500">
                        <Layers size={16} />
                      </span>
                      <div className="flex-1">
                        <Select
                          options={DOC_TYPE_OPTIONS}
                          value={DOC_TYPE_OPTIONS.find((o) => o.value === docType) || null}
                          onChange={(opt) => setDocType(opt?.value || "other")}
                          styles={selectStyles}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Cột phải: Tệp hồ sơ + Ghi chú nội bộ */}
              <div className="space-y-6">
              <section className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm">
                <SectionHeader
                  icon={UploadCloud}
                  tone="bg-red-50 text-red-600"
                  title="Tệp hồ sơ"
                  subtitle="Chứng từ, phụ lục đính kèm (bắt buộc)"
                />

                <input
                  id="dossier-upload-file"
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.xls,.xlsx"
                  className="hidden"
                  onChange={(e) => applyFile(e.target.files?.[0] || null)}
                />

                <label
                  htmlFor="dossier-upload-file"
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  className={`flex cursor-pointer items-center justify-center gap-4 rounded-[1.5rem] border-2 border-dashed px-6 py-8 text-center transition ${
                    isDragging
                      ? "border-red-400 bg-red-50/70"
                      : file
                        ? "border-emerald-200 bg-emerald-50/40"
                        : "border-gray-300 bg-gray-50 hover:border-red-300 hover:bg-red-50/40"
                  }`}
                >
                  {file ? (
                    <>
                      <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-600">
                        <FileCheck2 size={26} />
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="truncate text-sm font-bold text-gray-800">{file.name}</p>
                        <p className="mt-0.5 text-xs font-medium text-gray-400">
                          {formatFileSize(file.size)} · Bấm để đổi file
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="rounded-2xl bg-red-50 p-3 text-red-500">
                        <UploadCloud size={26} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-700">
                          Kéo thả hoặc bấm để chọn file
                        </p>
                        <p className="mt-0.5 text-xs font-medium text-gray-400">
                          Hỗ trợ PDF, DOCX, XLSX, JPG, PNG
                        </p>
                      </div>
                    </>
                  )}
                </label>

                {file && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 transition hover:text-red-600"
                    >
                      <Trash2 size={13} />
                      Bỏ chọn file
                    </button>
                  </div>
                )}
              </section>

              {/* Ghi chú nội bộ */}
              <section className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm">
                <SectionHeader
                  icon={StickyNote}
                  tone="bg-amber-50 text-amber-600"
                  title="Ghi chú nội bộ"
                  subtitle="Thông tin bổ sung cho nội bộ"
                />
                <textarea
                  rows={4}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className={`${inputBase} font-medium`}
                  placeholder="Mô tả ngắn về hồ sơ..."
                />
              </section>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-100 bg-white px-8 py-6 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-2xl border border-gray-200 px-5 py-3 text-sm font-bold text-gray-500 transition hover:bg-gray-50 hover:text-gray-700"
            >
              Hủy bỏ
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleSubmit}
              className={`inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3 text-sm font-bold text-white transition ${
                loading
                  ? "cursor-not-allowed bg-gray-300"
                  : "bg-slate-900 hover:scale-[1.02] active:scale-[0.99]"
              }`}
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <UploadCloud size={18} />}
              {loading ? "Đang tải lên..." : "Tải lên hồ sơ"}
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ContractLinkedDossierUploadModal;
